import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Users, User, Heart, CheckCircle2 } from 'lucide-react';
import { calculateBenefitPremium } from '@/components/utils/benefitPricing';

const COVERAGE_OPTIONS = [
  { value: 'employee_only', label: 'Just Me', icon: User, description: 'Individual coverage' },
  { value: 'employee_spouse', label: 'Me + Spouse', icon: Users, description: 'You and your spouse' },
  { value: 'employee_children', label: 'Me + Children', icon: Heart, description: 'You and your dependents' },
  { value: 'family', label: 'Family Plan', icon: Users, description: 'Full family coverage' }
];

export default function CoverageLevelModal({ open, onClose, product, employee, familyMembers, onAddToCart }) {
  const [selectedLevel, setSelectedLevel] = useState('employee_only');
  const [premiums, setPremiums] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (open && product && employee) {
      calculateAllPremiums();
    }
  }, [open, product, employee, familyMembers]);

  const calculateAllPremiums = async () => {
    setLoading(true);
    const calculated = {};
    
    for (const option of COVERAGE_OPTIONS) {
      try {
        const premium = await calculateBenefitPremium(
          product,
          employee,
          familyMembers,
          option.value
        );
        calculated[option.value] = premium;
      } catch (err) {
        calculated[option.value] = product.base_rate || 0;
      }
    }
    
    setPremiums(calculated);
    setLoading(false);
  };

  const handleAddToCart = () => {
    const premium = premiums[selectedLevel] || 0;
    onAddToCart(product, selectedLevel, premium);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="text-2xl">Choose Your Coverage</DialogTitle>
          <p className="text-slate-600 text-sm">Select who you'd like to cover with {product?.name}</p>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full" />
          </div>
        ) : (
          <div className="space-y-6 py-4">
            <RadioGroup value={selectedLevel} onValueChange={setSelectedLevel}>
              <div className="space-y-3">
                {COVERAGE_OPTIONS.map((option) => {
                  const Icon = option.icon;
                  const isDisabled = 
                    (option.value === 'employee_spouse' || option.value === 'family') && familyMembers.filter(m => m.relationship === 'spouse').length === 0 ||
                    (option.value === 'employee_children' || option.value === 'family') && familyMembers.filter(m => m.relationship === 'child').length === 0;
                  
                  return (
                    <div
                      key={option.value}
                      className={`relative flex items-center space-x-3 rounded-xl border-2 p-4 transition-all cursor-pointer ${
                        selectedLevel === option.value
                          ? 'border-emerald-500 bg-emerald-50'
                          : isDisabled
                            ? 'border-slate-200 bg-slate-50 opacity-50 cursor-not-allowed'
                            : 'border-slate-200 hover:border-emerald-300 bg-white'
                      }`}
                      onClick={() => !isDisabled && setSelectedLevel(option.value)}
                    >
                      <RadioGroupItem value={option.value} id={option.value} disabled={isDisabled} />
                      <Label
                        htmlFor={option.value}
                        className="flex-1 cursor-pointer flex items-center justify-between"
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                            selectedLevel === option.value ? 'bg-emerald-500' : 'bg-slate-100'
                          }`}>
                            <Icon className={`w-5 h-5 ${
                              selectedLevel === option.value ? 'text-white' : 'text-slate-600'
                            }`} />
                          </div>
                          <div>
                            <p className="font-medium text-slate-900">{option.label}</p>
                            <p className="text-sm text-slate-500">{option.description}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-xl font-bold text-slate-900">
                            ${(premiums[option.value] || 0).toFixed(2)}
                          </p>
                          <p className="text-xs text-slate-500">per month</p>
                        </div>
                      </Label>
                      {isDisabled && (
                        <Badge variant="outline" className="absolute top-2 right-2 text-xs">
                          Add family members first
                        </Badge>
                      )}
                    </div>
                  );
                })}
              </div>
            </RadioGroup>

            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button 
                onClick={handleAddToCart}
                className="bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600"
              >
                <CheckCircle2 className="w-4 h-4 mr-2" />
                Add to Enrollment
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}